<template>
    <div class="d-flex flex-column gap-2">
        <InputSelect
            v-model="option"
            :datasource="{ source: selectOptions }"
        />
        <div
            class="d-flex gap-3 flex-row flex-nowrap"
            v-if="showTimespan"
        >
            <InputTimespan
                v-model="internalValue1"
                format="TIMEMS3"
                mode="positive"
            />
            <span> - </span>
            <InputTimespan
                v-model="internalValue2"
                format="TIMEMS3"
                mode="positive"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { InputSelect, InputTimespan } from '@/components';
import { $tm } from '@/locize';
import type { SelectLine } from '@/objects';
import { StationRoleGPIOoperationValues, type StationRoleGPIOoperation } from '@/objects/generated';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import { computed, onMounted, ref, watch } from 'vue';

// we need to define 3 models

// The vmodel for the option (1, 2, 3, ...)
const option = defineModel<StationRoleGPIOoperation>('option', { required: true });

// The vmodel for the two values
const value1 = defineModel<number>('value1', { required: false });
const value2 = defineModel<number>('value2', { required: false });

/**
 * Internally in the system we assume that values over 70 is in seconds and belov 70 is in milliseconds
 * This function converts the value to milliseconds
 * @param value
 */
const getValueAsMilliseconds = (value?: number) => {
    if (!value) return 0;
    return value;
};

// We need some internal values to handle the timespan input
const internalValue1 = ref<ITimeSpan>(TimeSpan.FromMilliseconds(getValueAsMilliseconds(value1.value)));
const internalValue2 = ref<ITimeSpan>(TimeSpan.FromMilliseconds(getValueAsMilliseconds(value2.value)));

// The available options for the select
const selectOptions = ref<Array<SelectLine>>([]);

/**
 * This is the list of values that should show the timespan inputs
 */
const valuesThatShouldShowTimespan: Array<StationRoleGPIOoperation> = [
    'AttachToTime',
    'SetStartTimeNextGroup',
    'SetStartTimeParticipant',
    'AttachAndRoundToWholeMinute',
    'SetStartTimeParticipantWithBaasEqPort',
    'SetStartNextParticipantOrNextArmedHeat',
];

const showTimespan = computed(() => valuesThatShouldShowTimespan.includes(option.value));

watch([internalValue1, internalValue2], () => {
    // convert back to seconds or milliseconds
    value1.value = internalValue1.value ? internalValue1.value.TotalMilliseconds : 0;
    value2.value = internalValue2.value ? internalValue2.value.TotalMilliseconds : 0;

    // value1.value = val1 > 70000 ? Math.round(val1 / 1000) : Math.round(val1);
    // value2.value = val2 > 70000 ? Math.round(val2 / 1000) : Math.round(val2);
});

onMounted(() => {
    // Map deviceTypes to SelectLines
    let types: SelectLine[] = [];
    for (const key in StationRoleGPIOoperationValues) {
        types.push({
            text: `gpiooperation.${StationRoleGPIOoperationValues[key]}`.toLocaleLowerCase(),
            value: StationRoleGPIOoperationValues[key],
        });
    }

    types.sort((a, b) => $tm(a.text).localeCompare($tm(b.text)));

    selectOptions.value = types;
});
</script>
